import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { RoutePaths } from "../../common/RoutePaths";

const SWIPE_THRESHOLD = 50;

const SCREEN_NAME_TO_ROUTE = {
  home: RoutePaths.HOME,
  results: RoutePaths.RESULTS,
  rewards: RoutePaths.REWARDS,
  spin_wheel: RoutePaths.SPIN_WHEEL,
};

const PromoGraphic = ({ data = [] }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const touchStartX = useRef(0);

  // 1. Define the error handler at the top of the component
  const handleImageError = (e) => {
    const fallback = '/assets/images/extra/noBannerAvailable.png';
    // Check to prevent infinite loop if fallback image also fails
    if (e.target.src !== window.location.origin + fallback && e.target.src !== fallback) {
      e.target.src = fallback;
    }
  };

  useEffect(() => {
    if (data.length <= 1) return;

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % data.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [data.length]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (data.length <= 1) return;
    const endX = e.changedTouches[0].clientX;
    const delta = touchStartX.current - endX;
    if (delta > SWIPE_THRESHOLD) {
      setCurrentIndex((prev) => (prev + 1) % data.length);
    } else if (delta < -SWIPE_THRESHOLD) {
      setCurrentIndex((prev) => (prev - 1 + data.length) % data.length);
    }
  };

  if (!data || data.length === 0) {
    return (
      <div className="w-full h-40 sm:h-48 md:h-56 rounded-app bg-surface flex items-center justify-center">
        <p className="text-gray-400">No Promo Banners Available</p>
      </div>
    );
  }

  return (
    <div className="relative w-full  overflow-hidden">
      {/* Slides - touch swipe to change */}
      <div
        className="flex transition-transform duration-500 rounded-app ease-out h-full touch-pan-y"
        style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {data.map((banner, index) => {
          const img = (
            <img
              src={banner.imageUrl}
              alt={`Promo ${index + 1}`}
              onError={handleImageError}
              className="w-full h-full rounded-app object-contain"
            />
          );

          let linkTo = null;
          if (banner.actionType === "game_launch" && banner.gameName) {
            linkTo = RoutePaths.GAME_LOBBY.replace(":gameId", banner.gameName);
          } else if (banner.actionType === "in_app_navigation" && banner.screenName) {
            linkTo = SCREEN_NAME_TO_ROUTE[banner.screenName] || null;
          }

          return (
            <div key={index} className="min-w-full h-full">
              {linkTo ? (
                <Link to={linkTo} className="block w-full h-full">
                  {img}
                </Link>
              ) : (
                img
              )}
            </div>
          );
        })}
      </div>

      {data.length > 1 && (
        <>
          {/* Dots Indicator */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
            {data.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${idx === currentIndex ? "bg-white w-6" : "bg-white/40 hover:bg-white/60"
                  }`}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default PromoGraphic;